import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchDocs, isPagefindAvailable, type PagefindResultData } from '../lib/search';
import { useI18n, useTranslation } from '../i18n';

interface Props {
  /** Compact variant for the docs header. Inline variant is used on the home page. */
  compact?: boolean;
}

/**
 * In-house search UI backed by the Pagefind index.
 *
 * - Lazy-loads the index on first focus (no cost for users who never search)
 * - Debounces queries by 200 ms
 * - Keyboard nav: ArrowDown/ArrowUp through results, Enter to navigate, Escape to close
 * - Closes on click outside or on result selection
 */
export default function Search({ compact }: Props) {
  const t = useTranslation();
  const { locale } = useI18n();
  const navigate = useNavigate();

  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState<PagefindResultData[]>([]);
  const [open, setOpen]         = useState(false);
  const [highlighted, setH]     = useState(0);
  const [state, setState]       = useState<'idle' | 'loading' | 'ready' | 'unavailable'>('idle');

  const rootRef  = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Lazy-load the index the first time the input gets focus.
  // CRITICAL: must call isPagefindAvailable() — the prior implementation used
  // searchDocs('') which short-circuits to [] for empty queries before checking
  // the API, so it can't distinguish "loaded with no matches" from "never loaded".
  const ensureLoaded = useCallback(async () => {
    if (state !== 'idle') return;
    setState('loading');
    const ok = await isPagefindAvailable();
    setState(ok ? 'ready' : 'unavailable');
  }, [state]);

  // Debounced query execution.
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const id = window.setTimeout(async () => {
      try {
        const out = await searchDocs(query, locale, 8);
        setResults(out);
        setH(0);
      } catch {
        setResults([]);
      }
    }, 200);
    return () => window.clearTimeout(id);
  }, [query, locale]);

  // Close on outside click.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setH((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setH((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const hit = results[highlighted];
      if (hit) selectResult(hit);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const selectResult = (hit: PagefindResultData) => {
    setOpen(false);
    setQuery('');
    navigate(hit.url);
  };

  const showDropdown = open && (state === 'unavailable' || query.trim().length > 0);

  const placeholder = useMemo(() => t('nav.search'), [t]);

  return (
    <div ref={rootRef} className={compact ? 'search search-compact' : 'search'}>
      <input
        ref={inputRef}
        type="search"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        value={query}
        onFocus={() => { void ensureLoaded(); setOpen(true); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={onKey}
        className="search-input"
        aria-label={placeholder}
        aria-autocomplete="list"
        aria-expanded={showDropdown}
        aria-controls="search-results"
      />
      {showDropdown && (
        <div id="search-results" role="listbox" className="search-dropdown">
          {state === 'unavailable' && (
            <div className="search-empty">{t('search.unavailable')}</div>
          )}
          {state === 'ready' && results.length === 0 && query.trim() && (
            <div className="search-empty">{t('search.empty')}</div>
          )}
          {results.map((r, i) => (
            <button
              key={r.url}
              role="option"
              aria-selected={i === highlighted}
              className={i === highlighted ? 'search-hit search-hit-active' : 'search-hit'}
              onClick={() => selectResult(r)}
              onMouseEnter={() => setH(i)}
            >
              <div className="search-hit-title">{r.meta.title ?? r.url}</div>
              <div className="search-hit-excerpt" dangerouslySetInnerHTML={{ __html: r.excerpt }} />
              <div className="search-hit-url">{r.url}</div>
            </button>
          ))}
        </div>
      )}
      <style>{css}</style>
    </div>
  );
}

const css = `
.search { position: relative; width: 100%; }
.search-compact { width: 320px; max-width: 100%; }

.search-input {
  width: 100%;
  padding: 10px 14px;
  font-family: inherit;
  font-size: 0.95rem;
  color: var(--c-fg);
  background: var(--c-bg-alt);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  outline: none;
}
.search-input::placeholder { color: var(--c-muted); }
.search-input:focus {
  border-color: var(--c-accent);
  background: var(--c-bg);
}
.search-compact .search-input { padding: 6px 10px; font-size: 0.9rem; }

.search-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 50;
  background: var(--c-bg);
  border: 1px solid var(--c-border);
  border-radius: var(--radius);
  box-shadow: 0 12px 32px rgba(0,0,0,0.08);
  max-height: 60vh;
  overflow-y: auto;
}
.search-compact .search-dropdown {
  left: auto;
  right: 0;
  width: 480px;
  max-width: calc(100vw - 32px);
}

.search-empty { padding: 14px 16px; color: var(--c-muted); font-size: 0.9rem; }

.search-hit {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 0;
  border-bottom: 1px solid var(--c-border);
  padding: 12px 16px;
  cursor: pointer;
  font-family: inherit;
  color: var(--c-fg);
}
.search-hit:last-child { border-bottom: none; }
.search-hit:hover, .search-hit-active { background: var(--c-bg-alt); }

.search-hit-title {
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 2px;
}
.search-hit-excerpt {
  font-size: 0.85rem;
  color: var(--c-muted);
  line-height: 1.4;
  margin-bottom: 4px;
}
.search-hit-excerpt mark {
  background: rgba(52, 212, 191, 0.18);
  color: inherit;
  padding: 0 2px;
  border-radius: 2px;
}
.search-hit-url {
  font-size: 0.75rem;
  font-family: var(--font-mono);
  color: var(--c-muted);
}
`;
